import { Link, useLoaderData, useNavigate } from "react-router-dom";
import { Offer, OfferYacht } from "./model";
import { Card } from "../../components/wrappers/Card";
import { Button } from "../../components/ui/button";
import { copyToClipboard } from "../../lib/utils";
import { useToast } from "../../components/ui/use-toast";
import { Switch } from "../../components/ui/switch";

import { CopyIcon, Mail, ArrowUp, ArrowDown } from "lucide-react";
import { Separator } from "../../components/ui/separator";
import { useEffect, useState } from "react";
import { WithActionBar } from "../../components/wrappers/WithActionBar";
import { Content } from "../../components/wrappers/Content";
import { useStepsStore } from "./EditOffer/state";
import { OfferVersionsModal } from "./OfferVersionsModal";
import { useSearchParams, useLocation } from "react-router-dom";
import { Checkbox } from "../../components/ui/checkbox";
import { YachtOfferCard } from "./EditOffer/YachtOfferCard";
import { EngagementTimeline } from "./EngagementTimeline";
import { YachtDetailsModal } from "./EditOffer/YachtDetailsModal";
import { EmailsModal } from "./EditOffer/EditOfferForm";
import { fetch } from "@/src/lib/fetch";
import { useOfferData } from "@/src/hooks/useOfferData";
import clsx from "clsx";
import EditCustomerEmailModal from "../Bookings/Tabs/sections/UsersSection/EditCustomerEmailModal";
import { ConvertToBookingModal } from "./ConvertToBookingModal";
import { ArrowRight } from "lucide-react";

export const OfferDetails = () => {
  const [emailsModalOpen, setEmailsModalOpen] = useState(false);
  const [yachtDetailsOpen, setYachtDetailsOpen] = useState(false);
  const [yachtId, setYachtId] = useState<string | null>(null);
  const [versionsModalOpen, setVersionsModalOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const [url, setUrl] = useState("");
  const offerId = location.pathname.split("/").pop() ?? "";

  const [customerEmailModalOpen, setCustomerEmailModalOpen] = useState(false);
  const [convertOpen, setConvertOpen] = useState(false);

  const offerLoaded = useLoaderData() as Offer;
  const { offerData, usersData, yachtsData, feedbacksData, isLoadingYachts } = useOfferData(offerId);
  const offer = { ...offerData, ...usersData, ...yachtsData, ...feedbacksData };
  const [offerYachts, setOfferYachts] = useState<OfferYacht[]>(offer.yachts || []);
  const steps = useStepsStore();

  const additionalEmails = offer?.customer?.additionalEmails?.map((data: { email: string }) => data.email);

  const customerData = {
    email: offer.customer?.email ?? "",
    emails: additionalEmails,
  };

  const [searchParams, _] = useSearchParams();
  const { toast } = useToast();

  useEffect(() => {
    if (searchParams.get("modal") === "versions") {
      setVersionsModalOpen(true);
      navigate(location.pathname);
    }
    const origin = window.location.href.replace("/offers/add", "/offer/id_HERE");
    const baseUrl = offer.brand === "anchor" ? "https://anchor.yt" : "https://www.helm.yt";
    setUrl(`${baseUrl}/offer/${offer.id}`);
  }, [offer.id]);

  useEffect(() => {
    if (Array.isArray(offer.yachts) && offer.yachts.length) {
      const sorted = [...offer.yachts].sort((a, b) => a.order - b.order);
      setOfferYachts(sorted);
    }
  }, [offer.yachts]);

  const handleCopyToClipboard = (text: string) => {
    copyToClipboard(text);
    toast({
      title: "Copied!",
      variant: "default",
    });
  };

  const showVersionsModal = () => {
    setVersionsModalOpen(true);
  };

  const handleYachtDetailsModal = (id: string) => {
    setYachtId(id);
    setYachtDetailsOpen(true);
  };

  const showEditBtn = offer.status === "draft" || offer.status === "new_enquiry" || offer.status === "saved";

  // Convert to booking is a broker action — available on a saved or sent offer
  // (a client may accept by phone before the portal reaction), not only once
  // "sent". Hidden once a booking already exists.
  const canConvert =
    offer.status === "saved" ||
    offer.status === "broker_offer_sent" ||
    offer.status === "automatic_offer_sent";

  // Per-yacht client reactions, shown on the offer detail (matches the design's
  // "Yachts & client reactions"). Reactions only exist once the client engages.
  const REACTION_META: Record<string, { cls: string; label: string }> = {
    love: { cls: "bg-green-100 text-green-700", label: "❤ Loved" },
    maybe: { cls: "bg-amber-100 text-amber-700", label: "◐ Maybe" },
    pass: { cls: "bg-slate-100 text-slate-600", label: "✕ Passed" },
  };
  const anyReaction = (offerYachts ?? []).some((y) => (y as any).reaction);
  const reactionStatus = (r?: string | null) =>
    r === "love" ? "Accepted" : r === "maybe" ? "Viewed" : r === "pass" ? "Passed" : "—";

  const sortedByReaction = [...(offerYachts ?? [])].sort((a, b) => {
    const rank = (r?: string | null) => (r === "love" ? 0 : r === "maybe" ? 1 : r === "pass" ? 2 : 3);
    return rank(a.reaction) - rank(b.reaction);
  });

  // Earliest live hold across the offer's yachts — powers the rail countdown.
  const holdExpiry = (offerYachts ?? [])
    .map((y) => y.holdExpiresAt)
    .filter(Boolean)
    .map((d) => new Date(d as string))
    .filter((d) => d.getTime() > Date.now())
    .sort((a, b) => a.getTime() - b.getTime())[0];
  const holdLabel = holdExpiry
    ? (() => {
        const h = Math.round((holdExpiry.getTime() - Date.now()) / 3_600_000);
        return h >= 48 ? `in ${Math.round(h / 24)}d` : `in ${Math.max(1, h)}h`;
      })()
    : null;
  const sentDate = offer.dateUpdated || offer.enquiryDate || offer.dateCreated;
  const pickImg = (y: OfferYacht) =>
    (y.yacht?.media ?? []).find((m: string) => m && !m.includes("layout")) || null;

  const sendEmails = async (emails: string[]) => {
    await fetch(`/api/offers/${offer.id}/email`, "POST", emails).then(() => {
      toast({
        title: "Offer sent",
        description: "The offer has been sent",
        variant: "default",
      });
      setEmailsModalOpen(false);
      navigate(`/offers`);
    });
  };

  const moveYacht = async (from: number, to: number) => {
    if (to < 0 || to >= offerYachts.length) return;

    const updated = [...offerYachts];
    [updated[from], updated[to]] = [updated[to], updated[from]];

    const reordered = updated.map((yacht, index) => ({
      ...yacht,
      order: index,
    }));

    const sorted = [...reordered].sort((a, b) => a.order - b.order);
    setOfferYachts(sorted);

    const reorderedYachts = reordered.map((y) => ({
      id: y.id,
      order: y.order,
    }));

    try {
      await fetch(`/api/offers/${offerId}/reorder-yachts`, "PATCH", {
        yachts: reorderedYachts,
      });

      toast({
        title: "Yachts reordered",
        description: "The yacht order was successfully updated.",
        variant: "default",
      });
    } catch (error) {
      console.error("Failed to reorder yachts", error);
      toast({
        title: "Failed to reorder yachts",
        description: "There was a problem updating the yacht order.",
        variant: "destructive",
      });
    }
  };


  return (
    <WithActionBar actionBar={<></>}>
      <Content
        contentAction={
          <div className="flex items-center gap-6">
            {offer.status === "broker_offer_sent" && (
              <>
                {/* Editing a sent offer should reopen it as saved, on the configure step,
                    so the broker can add/remove/adjust yachts — not restart the create
                    flow at "add customer" with an empty basket. */}
                <Button
                  className="gap-2"
                  variant="default"
                  onClick={() => {
                    steps.setStep("Summary");
                    navigate(`/offers/edit/${offer.id}?step=Summary`);
                  }}
                >
                  Edit offer
                </Button>
                <Button asChild variant="link">
                  <Link to={"/offers/add/" + offer.id}>Create new revision</Link>
                </Button>
              </>
            )}
            {offer.hubspotId !== "NO_HUBSPOT_ID" && (
              <Button onClick={showVersionsModal} variant="link">
                See offer versions
              </Button>
            )}
            {canConvert && (
              <Button className="gap-2" variant="default" onClick={() => setConvertOpen(true)}>
                Convert to booking <ArrowRight size={16} />
              </Button>
            )}
          </div>
        }
        title="Offer details"
      >
        <div className="flex flex-col gap-6 items-start w-full">
          <div className="flex w-full">
            <span
              className={`flex py-1.5 px-3 rounded-[25px] justify-center mx-1 ${
                offer.status === "broker_offer_sent" ||
                offer.status === "automatic_offer_sent" ||
                offer.status === "new_booking"
                  ? "bg-green-200"
                  : offer.status === "new_enquiry"
                    ? "bg-red-200"
                    : "bg-blue-200"
              } `}
            >
              {offer.status && offer.status[0].toUpperCase()}
              {offer.status && offer.status.split("_").join(" ").substring(1)}
            </span>
            <span className={`flex py-1.5 px-3 rounded-[25px] justify-center mx-1 bg-orange-200`}>
              {offer.brand && offer.brand.charAt(0).toUpperCase() + offer.brand.slice(1).toLowerCase()}
            </span>
            {(offer.status === "saved" || offer.status === "broker_offer_sent" || offer.status === "draft") && (
              <>
                {(offer.status === "saved" || offer.status === "draft") && (
                  <Button
                    className="ml-auto"
                    onClick={() => {
                      steps.setStep("Customer");
                      navigate("/offers/edit/" + offer.id);
                    }}
                    variant="link"
                    size="sm"
                  >
                    Edit
                  </Button>
                )}
                {offer.status !== "draft" && (
                  <Button className="gap-2" size={"sm"} variant="action" onClick={() => setEmailsModalOpen(true)}>
                    Send to emails <Mail />
                  </Button>
                )}
              </>
            )}
          </div>

          {/* Design's two-column top: yachts & client reactions + engagement
              timeline on the left, next-step + client summary rail on the right. */}
          <div className="w-full grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-6 items-start">
            <div className="flex flex-col gap-6">
              <Card className="w-full">
                <h3 className="text-lg font-semibold mb-4">Yachts &amp; client reactions</h3>
                <div className="flex flex-col gap-3">
                  {sortedByReaction.map((oy) => (
                    <div key={oy.id} className="flex items-start gap-4 p-3 rounded-lg border border-blue-100">
                      <div className="w-24 h-16 rounded-md bg-slate-100 overflow-hidden shrink-0">
                        {pickImg(oy) && (
                          <img src={pickImg(oy)!} alt={oy.yacht?.name} className="w-full h-full object-cover" />
                        )}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="font-semibold">{oy.yacht?.name}</div>
                        <div className="text-sm text-slate-500">
                          {[oy.yacht?.builder, oy.yacht?.boatType, oy.yacht?.maxPeople ? `${oy.yacht.maxPeople} guests` : null]
                            .filter(Boolean)
                            .join(" · ")}
                        </div>
                        {oy.reactionNote && (
                          <div className="text-sm text-slate-600 mt-1 italic">
                            “{oy.reactionNote}”<span className="not-italic text-slate-400"> — {offer.customer?.firstName}</span>
                          </div>
                        )}
                      </div>
                      <div className="text-right shrink-0">
                        {oy.reaction && REACTION_META[oy.reaction] ? (
                          <span className={clsx("inline-flex items-center rounded-full px-3 py-1 text-sm font-medium", REACTION_META[oy.reaction].cls)}>
                            {REACTION_META[oy.reaction].label}
                          </span>
                        ) : (
                          <span className="inline-flex items-center rounded-full px-3 py-1 text-sm font-medium bg-slate-100 text-slate-500">
                            No reaction
                          </span>
                        )}
                        <div className="text-xs text-slate-400 mt-1">{reactionStatus(oy.reaction)}</div>
                      </div>
                    </div>
                  ))}
                  {sortedByReaction.length === 0 && (
                    <p className="text-sm text-slate-500">Yachts on this offer will appear here with the client’s reactions once they engage.</p>
                  )}
                </div>
              </Card>
              {offer.id && <EngagementTimeline offerId={offer.id} />}
            </div>

            <div className="flex flex-col gap-6">
              {canConvert && (
                <Card className="w-full bg-blue-50/60 border-blue-100">
                  <p className="text-xs uppercase tracking-wide text-blue-600 font-medium">Next step</p>
                  <p className="text-sm text-slate-600 mt-2 mb-4">
                    {anyReaction
                      ? "The client has reacted — convert to lock the yacht and open the money & ops record."
                      : "Pick the yacht the client is going ahead with and convert."}
                  </p>
                  <Button className="gap-2 w-full justify-center" onClick={() => setConvertOpen(true)}>
                    Convert to booking <ArrowRight size={16} />
                  </Button>
                </Card>
              )}
              <Card className="w-full">
                <h3 className="text-lg font-semibold mb-4">Client</h3>
                <div className="flex flex-col">
                  <div className="flex items-center justify-between py-3 border-b border-blue-100">
                    <span className="text-slate-500">Client</span>
                    <span className="font-medium">{[offer.customer?.firstName, offer.customer?.lastName].filter(Boolean).join(" ") || "—"}</span>
                  </div>
                  <div className="flex items-center justify-between py-3 border-b border-blue-100">
                    <span className="text-slate-500">Broker</span>
                    <span className="font-medium">{offer.broker?.fullName || "—"}</span>
                  </div>
                  <div className="flex items-center justify-between py-3 border-b border-blue-100">
                    <span className="text-slate-500">Sent</span>
                    <span className="font-medium">
                      {sentDate ? new Date(sentDate).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : "—"}
                    </span>
                  </div>
                  {holdLabel && (
                    <div className="flex items-center justify-between py-3">
                      <span className="text-slate-500">Hold expires</span>
                      <span className="inline-flex items-center rounded-full bg-amber-100 text-amber-700 px-3 py-0.5 text-sm font-medium">{holdLabel}</span>
                    </div>
                  )}
                </div>
                {offer.hubspotId !== "NO_HUBSPOT_ID" && offer.dealLink && (
                  <a
                    href={offer.dealLink}
                    target="_blank"
                    rel="noreferrer"
                    className="mt-4 flex items-center justify-center gap-2 rounded-md border border-blue-100 py-2.5 text-sm font-medium text-blue-600 hover:bg-blue-50"
                  >
                    Open contact in HubSpot ↗
                  </a>
                )}
              </Card>
            </div>
          </div>

          <Card className="w-full">
            <div className="flex justify-between items-center mb-8">
              <h3 className="text-lg font-semibold">Customer details</h3>
              <div className="flex items-center ">
                {showEditBtn && (
                  <>
                    <Button
                      onClick={() => {
                        steps.setStep("Customer");
                        navigate("/offers/edit/" + offer.id);
                      }}
                      variant="link"
                      size="sm"
                      className="text-blue-600 pr-[8px]"
                    >
                      Edit offer details
                    </Button>
                    <span className="text-sm text-neutral-300 pr-[8px]">|</span>
                  </>
                )}
                <>
                  <div className="flex justify-between items-center">
                    <Button
                      onClick={() => setCustomerEmailModalOpen(true)}
                      variant="link"
                      className="text-blue-600 px-0"
                    >
                      Edit customer e-mail
                    </Button>
                    <EditCustomerEmailModal
                      defaultValues={customerData}
                      offerId={offer.id}
                      open={customerEmailModalOpen}
                      onOpenChange={setCustomerEmailModalOpen}
                    />
                  </div>
                </>
              </div>
            </div>
            <div
              className={`grid ${additionalEmails && additionalEmails?.length > 0 ? "grid-cols-5" : "grid-cols-4"} gap-6`}
            >
              <div className="flex flex-col gap-2 items-stretch">
                <span className="text-base font-normal">Name</span>
                <span title={offer.customer?.firstName} className="text-base font-medium line-clamp-1 break-all">
                  {offer.customer?.firstName}
                </span>
              </div>
              <div className="flex flex-col gap-2 items-stretch">
                <span className="text-base font-normal">Surname</span>
                <span title={offer.customer?.lastName} className="text-base font-medium line-clamp-1 break-all">
                  {offer.customer?.lastName}
                </span>
              </div>
              <div className="flex flex-col gap-2 items-stretch">
                <span className="text-base font-normal">E-mail</span>
                <span title={offer.customer?.email} className="text-base font-medium line-clamp-1 break-all">
                  {offer.customer?.email}
                </span>
              </div>
              {additionalEmails && additionalEmails?.length > 0 && (
                <div className="flex flex-col gap-2 items-stretch">
                  <span className="text-base font-normal">Additional e-mails</span>
                  {offer.customer?.additionalEmails?.map((data: { email: string }) => (
                    <span title={data.email} className="text-base font-medium line-clamp-1 break-all" key={data.email}>
                      {data.email}
                    </span>
                  ))}
                </div>
              )}
              <div className="flex flex-col gap-2 items-stretch">
                <span className="text-base font-normal">Phone number</span>
                <span title={offer.customer?.email} className="text-base font-medium line-clamp-1 break-all">
                  {offer.customer?.mobile}
                </span>
              </div>
            </div>
          </Card>
          <div className="flex w-full gap-6">
            <Card className="flex flex-col basis-[428px] grow gap-6">
              <h3 className="text-lg font-semibold">Offer link</h3>
              <span
                onClick={(e) => {
                  handleCopyToClipboard(e.currentTarget.innerText);
                }}
                className="flex items-center text-normal text-base gap-2"
              >
                <span className="line-clamp-1 break-all">{url}</span>{" "}
                <CopyIcon size={24} className="stroke-slate-500 min-w-6" />
              </span>
            </Card>
            <Card className="flex flex-col basis-[664px] grow gap-6">
              <h3 className="text-lg font-semibold">Deal link</h3>
              <span
                onClick={(e) => {
                  handleCopyToClipboard(e.currentTarget.innerText);
                }}
                className="flex items-center text-normal text-base gap-2"
              >
                <span className="line-clamp-1 break-all">
                  {offer.hubspotId === "NO_HUBSPOT_ID" || !offer.dealLink ? "No related deal" : offer.dealLink}
                </span>{" "}
                {offer.hubspotId === "NO_HUBSPOT_ID" || !offer.dealLink ? null : (
                  <CopyIcon size={24} className="stroke-slate-500 min-w-6" />
                )}
              </span>
            </Card>
          </div>
          {!!offer.feedbacks?.length && (
            <Card className="w-full">
              <div className="flex flex-col">
                <h3 className="text-lg font-semibold mb-6">Offer feedback</h3>
                <div className="flex flex-col gap-4">
                  {offer.feedbacks?.map((feedback, index) => {
                    return (
                      <div key={index} className="flex flex-col border-b border-blue-100 last-of-type:border-none">
                        {!!feedback?.options?.length && (
                          <div className="flex flex-col">
                            <p className="text-base font-normal">
                              {feedback.type === "request_extras" && "Client requested following extras"}
                              {feedback.type === "feedback" &&
                                "How did you like given options? Is there anything to improve on our side?"}
                              {feedback.type === "change_request" && "Client requested offer change"}
                            </p>
                            <ul className="flex flex-col px-2 gap-2 mt-2">
                              {feedback?.options?.map((info) => {
                                return (
                                  <li className="text-base font-normal flex items-center gap-2 py-1.5">
                                    <Checkbox checked disabled className="w-6 h-6" />
                                    {info.value}
                                  </li>
                                );
                              })}
                            </ul>
                          </div>
                        )}
                        <p className="text-base font-medium mt-3">{feedback.message}</p>
                      </div>
                    );
                  })}
                </div>
              </div>
            </Card>
          )}
          <div className="flex w-full gap-6">
            <Card className="flex flex-col basis-[664px] grow gap-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold">Mailing settings</h3>
                {showEditBtn && (
                  <Button
                    onClick={() => {
                      steps.setStep("Customer");
                      navigate("/offers/edit/" + offer.id);
                    }}
                    variant="link"
                    size="sm"
                  >
                    Edit
                  </Button>
                )}
              </div>
              <div className="flex flex-col gap-6">
                <div className="flex items-center justify-between">
                  <span className="text-base font-normal">Create booking notification: </span>
                  <Switch disabled checked={offer?.mailingSettings?.bookingConfirmation} />
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-base font-normal">Booking updates notifications: </span>
                  <Switch disabled checked={offer?.mailingSettings?.bookingUpdates} />
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-base font-normal">
                    Payment confirmation notifications (for manually handled payments in HELMs App):{" "}
                  </span>
                  <Switch disabled checked={offer?.mailingSettings?.paymentConfirmations} />
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-base font-normal">Payment reminders notifications: </span>
                  <Switch disabled checked={offer?.mailingSettings?.paymentReminders} />
                </div>
              </div>
            </Card>
            <div className="flex flex-col basis-[664px] grow gap-6"></div>
          </div>
          <Card className="w-full">
            <div className="flex justify-between items-center mb-8">
              <h3 className="text-lg font-semibold">Description</h3>
              {showEditBtn && (
                <Button
                  onClick={() => {
                    steps.setStep("Description");
                    navigate("/offers/edit/" + offer.id);
                  }}
                  variant="link"
                  size="sm"
                >
                  Edit
                </Button>
              )}
            </div>
            <div className="flex gap-x-6">
              <div className="flex flex-col gap-y-6 basis-[620px] grow">
                <div className="flex flex-col gap-2 items-stretch">
                  <span className="text-base font-normal">Offer title</span>
                  <span className="text-base font-medium">{offer.title}</span>
                </div>
                <div className="flex flex-col gap-2 items-stretch">
                  <span className="text-base font-normal">Intro</span>
                  <span className="text-base font-medium">{offer.intro}</span>
                </div>
              </div>
              <Separator className="h-auto" orientation="vertical" />
              <div className="flex flex-col gap-y-6 basis-[400px] grow">
                <div className="flex flex-col gap-2 items-stretch">
                  <span className="text-base font-normal">Currency</span>
                  <span className="text-base font-medium">{offer.currency?.name}</span>
                </div>
                <div className="flex flex-col gap-2 items-stretch">
                  <span className="text-base font-normal">Charter type</span>
                  <span className="text-base font-medium">{offer.charterType}</span>
                </div>
                <div className="flex flex-col gap-2 items-stretch">
                  <span className="text-base font-normal">Enquiry date</span>
                  <span className="text-base font-medium">
                    {offer.enquiryDate
                      ? new Date(offer.enquiryDate).toLocaleDateString("en-EN", {
                          year: "numeric",
                          month: "2-digit",
                          day: "2-digit",
                        })
                      : "N/A"}
                  </span>
                </div>
                <div className="flex flex-col gap-2 items-stretch">
                  <span className="text-base font-normal">Broker (Booking owner)</span>
                  <span className="text-base font-medium">{offer?.broker?.fullName}</span>
                </div>
              </div>
            </div>
          </Card>
          <Card className="w-full">
            <div className="flex align-center gap-6">
              <h3 className="text-lg font-semibold">Selected yachts</h3>
              {showEditBtn && (
                <Button
                  className="ml-auto"
                  onClick={(e) => {
                    e.preventDefault();
                    navigate(`/offers/edit/${offer.id}`);
                    steps.setStep("Yachts");
                  }}
                  variant="link"
                >
                  Edit
                </Button>
              )}
            </div>
            <div className="flex flex-col gap-4 py-6">
              {isLoadingYachts ? (
                <div className="flex items-center justify-center">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className={clsx("animate-spin", "w-10 h-10")}
                  >
                    <path d="M21 12a9 9 0 1 1-6.219-8.56" />
                  </svg>
                </div>
              ) : (
                offerYachts.map((offerYacht: OfferYacht, index) => (
                  <div key={offerYacht.id}>
                    <div className="flex flex-col p-4 border rounded-lg border-blue-100">
                      <div className="flex mb-4">
                        <Button
                          className="bg-transparent text-blue-600 hover:bg-transparent pl-0 pr-2 items-center align-middle font-medium"
                          onClick={() => moveYacht(index, index - 1)}
                        >
                          <ArrowUp size={16} className="stroke-blue-600 mr-2" />
                          Move up
                        </Button>
                        <Button
                          className="bg-transparent text-blue-600 hover:bg-transparent font-medium pl-2"
                          onClick={() => moveYacht(index, index + 1)}
                        >
                          <ArrowDown size={16} className="stroke-blue-600 mr-2" />
                          Move down
                        </Button>
                      </div>
                      <YachtOfferCard
                        {...offerYacht}
                        openYachtDetails={() => handleYachtDetailsModal(offerYacht.yacht.id)}
                        currency={offer?.currency?.code}
                      />
                    </div>
                  </div>
                ))
              )}
            </div>
          </Card>
          {yachtDetailsOpen && (
            <YachtDetailsModal yachtDetailsId={yachtId} open={yachtDetailsOpen} setOpen={setYachtDetailsOpen} />
          )}
        </div>
      </Content>
      {offer.id && (
        <ConvertToBookingModal
          offer={offer as Offer}
          yachts={offerYachts}
          open={convertOpen}
          onOpenChange={setConvertOpen}
        />
      )}
      <OfferVersionsModal hubspotId={offer.hubspotId} open={versionsModalOpen} setOpen={setVersionsModalOpen} />
      <EmailsModal
        defaultEmail={offer.customer?.email ?? ""}
        hideSaveBtn
        onSubmit={(emails) => sendEmails(emails)}
        modalOpen={emailsModalOpen}
        setModalOpen={setEmailsModalOpen}
      />
    </WithActionBar>
  );
};
